/**
 * Per-corner fillet (rounded interior corners) for plan-editor walls.
 * Works in plan pixels; callers convert to world meters for 3D.
 */

import { inferArcClockwise } from "./wall_geometry.js";
import { floorVertexRing } from "./floor_polygon.js";
import { normalizeFloorPolygon } from "./floor_mesh.js";

const EPS = 1e-6;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/** Incident edges at a plan-editor node. */
export function nodeIncidents(model, nodeId) {
  const out = [];
  for (const e of model?.edges ?? []) {
    if (e.a === nodeId) out.push({ neighborId: e.b, edgeId: e.id, edge: e, atStart: false });
    else if (e.b === nodeId) out.push({ neighborId: e.a, edgeId: e.id, edge: e, atStart: true });
  }
  return out;
}

/**
 * Compute fillet trim points + arc center in pixel space.
 * @param {{ px, py }} P corner
 * @param {{ px, py }} A neighbor on first wall (A–P)
 * @param {{ px, py }} C neighbor on second wall (P–C)
 * @param {number} rPx fillet radius in pixels
 */
export function computeCornerFilletPx(P, A, C, rPx) {
  if (!(rPx > EPS)) return null;
  const u1x = P.px - A.px;
  const u1y = P.py - A.py;
  const u2x = C.px - P.px;
  const u2y = C.py - P.py;
  const l1 = Math.hypot(u1x, u1y);
  const l2 = Math.hypot(u2x, u2y);
  if (l1 < 4 || l2 < 4) return null;
  const u1 = { x: u1x / l1, y: u1y / l1 };
  const u2 = { x: u2x / l2, y: u2y / l2 };
  const dot = clamp(u1.x * u2.x + u1.y * u2.y, -1, 1);
  const theta = Math.acos(dot);
  if (theta < 0.08 || theta > Math.PI - 0.08) return null;
  const idealTrim = rPx / Math.tan(theta / 2);
  const maxTrim = Math.min(l1, l2) * 0.45;
  const t = Math.min(idealTrim, maxTrim);
  if (t < 0.5) return null;
  const effectiveR = t / Math.tan(theta / 2);
  const t1 = { px: P.px - u1.x * t, py: P.py - u1.y * t };
  const t2 = { px: P.px + u2.x * t, py: P.py + u2.y * t };
  const bx = u2.x - u1.x;
  const by = u2.y - u1.y;
  const bl = Math.hypot(bx, by) || 1;
  const bis = { x: bx / bl, y: by / bl };
  const centerDist = effectiveR / Math.sin(theta / 2);
  const center = { px: P.px + bis.x * centerDist, py: P.py + bis.y * centerDist };
  return { t1, t2, center, rPx: effectiveR, trim: t, theta };
}

const round3 = (v) => Math.round(v * 1000) / 1000;

function inwardNormal(ax, az, bx, bz, room) {
  const len = Math.hypot(bx - ax, bz - az) || 1;
  let nx = -(bz - az) / len;
  let nz = (bx - ax) / len;
  const b = room?.bounds;
  if (b) {
    const cx = (b.min[0] + b.max[0]) / 2;
    const cz = (b.min[2] + b.max[2]) / 2;
    const mx = (ax + bx) / 2;
    const mz = (az + bz) / 2;
    if ((cx - mx) * nx + (cz - mz) * nz < 0) {
      nx = -nx;
      nz = -nz;
    }
  }
  return [round3(nx), round3(nz)];
}

/**
 * Build 2D wall strokes (straight segments + arc paths) for SVG render.
 * @returns {{ lines: {x1,y1,x2,y2,edgeId}[], arcs: {t1,t2,center,r,edgeId}[] }}
 */
export function planWallStrokes(model) {
  const mpp = model?.metersPerPixel > 0 ? model.metersPerPixel : 0.02;
  const trimKey = (nodeId, neighborId) => `${nodeId}:${neighborId}`;
  const trimPx = new Map();
  const arcs = [];

  for (const [, n] of model?.nodes ?? []) {
    const rM = Number(n.filletRadius) || 0;
    if (rM <= 0) continue;
    const inc = nodeIncidents(model, n.id);
    if (inc.length !== 2) continue;
    const A = model.nodes.get(String(inc[0].neighborId));
    const C = model.nodes.get(String(inc[1].neighborId));
    if (!A || !C) continue;
    const fil = computeCornerFilletPx(n, A, C, rM / mpp);
    if (!fil) continue;
    trimPx.set(trimKey(n.id, inc[0].neighborId), fil.t1);
    trimPx.set(trimKey(n.id, inc[1].neighborId), fil.t2);
    arcs.push({ ...fil, nodeId: n.id });
  }

  const lines = [];
  for (const e of model?.edges ?? []) {
    const A = model.nodes.get(String(e.a));
    const B = model.nodes.get(String(e.b));
    if (!A || !B) continue;
    const start = trimPx.get(trimKey(e.a, e.b)) ?? { px: A.px, py: A.py };
    const end = trimPx.get(trimKey(e.b, e.a)) ?? { px: B.px, py: B.py };
    if (Math.hypot(end.px - start.px, end.py - start.py) < 0.5) continue;
    lines.push({ x1: start.px, y1: start.py, x2: end.px, y2: end.py, edgeId: e.id });
  }
  return { lines, arcs };
}

/**
 * Expand plan model to world-space wall records (straight + fillet arcs) for 3D.
 * @param {object} model
 * @param {object} room live scene room
 * @param {(px:number, py:number) => { x:number, z:number }} pxToWorld
 */
export function expandWallsFromModel(model, room, pxToWorld) {
  if (!model || !room) return [];
  const mpp = model.metersPerPixel > 0 ? model.metersPerPixel : 0.02;
  const trimKey = (nodeId, neighborId) => `${nodeId}:${neighborId}`;
  const trimWorld = new Map();
  const arcCorners = [];
  const height = room.dimensions?.height ?? model.height ?? 2.8;

  for (const [, n] of model.nodes) {
    const rM = Number(n.filletRadius) || 0;
    if (rM <= 0) continue;
    const inc = nodeIncidents(model, n.id);
    if (inc.length !== 2) continue;
    const A = model.nodes.get(String(inc[0].neighborId));
    const C = model.nodes.get(String(inc[1].neighborId));
    if (!A || !C) continue;
    const fil = computeCornerFilletPx(n, A, C, rM / mpp);
    if (!fil) continue;
    const w1 = pxToWorld(fil.t1.px, fil.t1.py);
    const w2 = pxToWorld(fil.t2.px, fil.t2.py);
    const wc = pxToWorld(fil.center.px, fil.center.py);
    trimWorld.set(trimKey(n.id, inc[0].neighborId), [round3(w1.x), round3(w1.z)]);
    trimWorld.set(trimKey(n.id, inc[1].neighborId), [round3(w2.x), round3(w2.z)]);
    arcCorners.push({
      nodeId: n.id,
      start: [round3(w1.x), round3(w1.z)],
      end: [round3(w2.x), round3(w2.z)],
      center: [round3(wc.x), round3(wc.z)],
    });
  }

  const walls = [];
  for (const edge of model.edges ?? []) {
    const A = model.nodes.get(String(edge.a));
    const B = model.nodes.get(String(edge.b));
    if (!A || !B) continue;
    const wa = pxToWorld(A.px, A.py);
    const wb = pxToWorld(B.px, B.py);
    const ax = trimWorld.get(trimKey(edge.a, edge.b))?.[0] ?? round3(wa.x);
    const az = trimWorld.get(trimKey(edge.a, edge.b))?.[1] ?? round3(wa.z);
    const bx = trimWorld.get(trimKey(edge.b, edge.a))?.[0] ?? round3(wb.x);
    const bz = trimWorld.get(trimKey(edge.b, edge.a))?.[1] ?? round3(wb.z);
    if (Math.hypot(bx - ax, bz - az) < 0.02) continue;

    let existing = edge.sceneWallId ? room.walls?.find((w) => w.id === edge.sceneWallId) : null;
    const id = existing?.id ?? edge.sceneWallId ?? `w_pe_${edge.id}`;
    edge.sceneWallId = id;
    const isExterior = /^w_(south|north|east|west|ext)/.test(id) || Boolean(existing?.view);
    walls.push({
      id,
      edgeId: `E${edge.id}`,
      start: [ax, az],
      end: [bx, bz],
      height,
      thickness: existing?.thickness ?? (isExterior ? 0.15 : 0.1),
      normal: inwardNormal(ax, az, bx, bz, room),
      ...(existing?.view ? { view: true, viewLength: existing.viewLength } : {}),
    });
  }

  const b = room.bounds;
  const interiorRef = b ? [(b.min[0] + b.max[0]) / 2, (b.min[2] + b.max[2]) / 2] : null;
  for (const arc of arcCorners) {
    const clockwise = inferArcClockwise(arc.start, arc.end, arc.center, interiorRef);
    walls.push({
      id: `w_fillet_${arc.nodeId}`,
      edgeId: `F${arc.nodeId}`,
      start: arc.start,
      end: arc.end,
      height,
      thickness: 0.15,
      kind: "arc",
      arc: { center: arc.center, clockwise },
      normal: inwardNormal(arc.start[0], arc.start[1], arc.end[0], arc.end[1], room),
    });
  }
  return walls;
}

const FLOOR_CORNER_ORDER = ["nw", "ne", "se", "sw"];

/** Incident wall segments at each floor-rect corner (plan pixels). */
export const FLOOR_CORNER_NEIGHBORS = {
  nw: (pts) => ({ P: pts.nw, A: pts.sw, C: pts.ne }),
  ne: (pts) => ({ P: pts.ne, A: pts.nw, C: pts.se }),
  se: (pts) => ({ P: pts.se, A: pts.ne, C: pts.sw }),
  sw: (pts) => ({ P: pts.sw, A: pts.se, C: pts.nw }),
};

function normalizedFloorRect(floor) {
  const x0 = Math.min(floor.x0, floor.x1);
  const y0 = Math.min(floor.y0, floor.y1);
  const x1 = Math.max(floor.x0, floor.x1);
  const y1 = Math.max(floor.y0, floor.y1);
  return { x0, y0, x1, y1 };
}

function floorCornerPoints(floor) {
  const c = floor?.corners;
  if (
    c?.nw &&
    c?.ne &&
    c?.se &&
    c?.sw &&
    [c.nw, c.ne, c.se, c.sw].every((p) => Number.isFinite(Number(p.px)) && Number.isFinite(Number(p.py)))
  ) {
    return {
      nw: { px: Number(c.nw.px), py: Number(c.nw.py) },
      ne: { px: Number(c.ne.px), py: Number(c.ne.py) },
      se: { px: Number(c.se.px), py: Number(c.se.py) },
      sw: { px: Number(c.sw.px), py: Number(c.sw.py) },
    };
  }
  const { x0, y0, x1, y1 } = normalizedFloorRect(floor);
  return {
    nw: { px: x0, py: y0 },
    ne: { px: x1, py: y0 },
    se: { px: x1, py: y1 },
    sw: { px: x0, py: y1 },
  };
}

function defaultFloorFillet(floor) {
  return { nw: 0, ne: 0, se: 0, sw: 0, ...(floor?.fillet ?? {}) };
}

/**
 * Corner ids in CCW perimeter order around the live quad centroid.
 * Semantic nw/ne/se/sw labels can drift after free corner drags — geometric
 * order keeps fill and strokes inside the green edge sticks.
 */
function floorPerimeterCornerIds(floor) {
  const pts = floorCornerPoints(floor);
  const entries = FLOOR_CORNER_ORDER.map((id) => ({ id, px: pts[id].px, py: pts[id].py }));
  let cx = 0;
  let cy = 0;
  for (const e of entries) {
    cx += e.px;
    cy += e.py;
  }
  cx /= entries.length;
  cy /= entries.length;
  entries.sort((a, b) => Math.atan2(a.py - cy, a.px - cx) - Math.atan2(b.py - cy, b.px - cx));
  let area = 0;
  for (let i = 0; i < entries.length; i++) {
    const a = entries[i];
    const b = entries[(i + 1) % entries.length];
    area += a.px * b.py - b.px * a.py;
  }
  if (area < 0) entries.reverse();
  return entries.map((e) => e.id);
}

/**
 * Walk a floor rectangle outline (straight edges + optional corner fillets).
 * @param {object} floor { x0,y0,x1,y1, fillet? }
 * @param {number} mpp meters per pixel
 * @param {number} [arcSegments=8]
 */
function walkFloorOutline(floor, mpp, arcSegments = 8) {
  if (!floor) return { lines: [], arcs: [], fillPoints: [], pathD: "" };
  const mppSafe = mpp > 0 ? mpp : 0.02;
  const fillet = defaultFloorFillet(floor);
  const ringVerts = floor.vertices?.length >= 3 ? floorVertexRing(floor) : null;
  const order = ringVerts ? null : floorPerimeterCornerIds(floor);
  const pts = ringVerts ? null : floorCornerPoints(floor);
  const cornerCount = ringVerts?.length ?? order.length;
  const lines = [];
  const arcs = [];
  const fillPoints = [];
  const pathParts = [];
  let prevExit = null;
  let firstEntry = null;

  for (let i = 0; i < cornerCount; i++) {
    let id;
    let P;
    let A;
    let C;
    if (ringVerts) {
      id = ringVerts[i].id;
      P = ringVerts[i];
      A = ringVerts[(i - 1 + cornerCount) % cornerCount];
      C = ringVerts[(i + 1) % cornerCount];
    } else {
      id = order[i];
      const prevId = order[(i - 1 + cornerCount) % cornerCount];
      const nextId = order[(i + 1) % cornerCount];
      P = pts[id];
      A = pts[prevId];
      C = pts[nextId];
    }
    const rPx = (Number(fillet[id]) || 0) / mppSafe;
    const fil = computeCornerFilletPx(P, A, C, rPx);
    const entry = fil ? fil.t1 : P;
    const exit = fil ? fil.t2 : P;
    if (!firstEntry) firstEntry = entry;
    if (prevExit) {
      lines.push({ x1: prevExit.px, y1: prevExit.py, x2: entry.px, y2: entry.py, cornerId: id });
      pathParts.push(`L ${entry.px} ${entry.py}`);
    } else {
      pathParts.push(`M ${entry.px} ${entry.py}`);
    }
    if (fil) {
      const sweep =
        (fil.t2.px - fil.t1.px) * (fil.center.py - fil.t1.py) -
          (fil.t2.py - fil.t1.py) * (fil.center.px - fil.t1.px) <
        0
          ? 1
          : 0;
      arcs.push({ ...fil, cornerId: id, sweep });
      pathParts.push(`A ${fil.rPx} ${fil.rPx} 0 0 ${sweep} ${exit.px} ${exit.py}`);
      const steps = Math.max(2, arcSegments);
      fillPoints.push([entry.px, entry.py]);
      for (let k = 1; k <= steps; k++) {
        const t = k / steps;
        const a0 = Math.atan2(fil.t1.py - fil.center.py, fil.t1.px - fil.center.px);
        let a1 = Math.atan2(fil.t2.py - fil.center.py, fil.t2.px - fil.center.px);
        let da = a1 - a0;
        if (sweep === 0) {
          while (da > Math.PI) da -= 2 * Math.PI;
          while (da < -Math.PI) da += 2 * Math.PI;
        } else {
          while (da <= 0) da += 2 * Math.PI;
          while (da > 2 * Math.PI) da -= 2 * Math.PI;
        }
        const th = a0 + da * t;
        fillPoints.push([fil.center.px + fil.rPx * Math.cos(th), fil.center.py + fil.rPx * Math.sin(th)]);
      }
    } else {
      fillPoints.push([entry.px, entry.py]);
    }
    prevExit = exit;
  }
  if (prevExit && firstEntry) {
    lines.push({
      x1: prevExit.px,
      y1: prevExit.py,
      x2: firstEntry.px,
      y2: firstEntry.py,
      cornerId: "close",
    });
  }
  pathParts.push("Z");
  const pathD = pathParts.join(" ");

  return { lines, arcs, fillPoints, pathD };
}

/**
 * Build 2D floor strokes (straight edges + corner arcs + fill polygon) for SVG.
 * @returns {{ lines: object[], arcs: object[], fillPoints: number[][] }}
 */
export function planFloorStrokes(floor, mpp) {
  return walkFloorOutline(floor, mpp);
}

/** Closed fill ring in plan pixels — follows the same outline as the green edge sticks. */
export function planFloorFillPoints(floor, mpp) {
  if (!floor) return [];
  return walkFloorOutline(floor, mpp).fillPoints;
}

/**
 * Closed floor polygon in world meters [[x,z], ...] for 3D room data.
 * Uses the vertex ring directly so 3D matches the 2D plan outline exactly.
 */
export function floorPolygonMeters(floor, pxToWorld, mpp, arcSegments = 8) {
  if (floor?.vertices?.length >= 3) {
    const ring = floorVertexRing(floor);
    const out = ring.map((v) => {
      const w = pxToWorld(v.px, v.py);
      return [round3(w.x), round3(w.z)];
    });
    return normalizeFloorPolygon(out);
  }
  const { fillPoints } = walkFloorOutline(floor, mpp, arcSegments);
  if (!fillPoints.length) return [];
  const out = fillPoints.map(([px, py]) => {
    const w = pxToWorld(px, py);
    return [round3(w.x), round3(w.z)];
  });
  if (out.length >= 3) {
    const [ax, az] = out[0];
    const [bx, bz] = out[out.length - 1];
    if (Math.hypot(ax - bx, az - bz) > 0.001) out.push([ax, az]);
  }
  return out;
}

export default {
  nodeIncidents,
  computeCornerFilletPx,
  planWallStrokes,
  expandWallsFromModel,
  FLOOR_CORNER_NEIGHBORS,
  planFloorStrokes,
  planFloorFillPoints,
  floorPolygonMeters,
};
